import { Dayjs } from "dayjs"
import { IDashboardStudentClass } from "ts/interfaces/AdminDashboard"
import { DashboardPeriodPreset } from "ts/types/AdminDashboard"
import dayjs from "utils/dayjs"

const MINUS = "−"
const RETENTION_MONTHS = 24

const withSign = (value: number, dollars: number, text: string) =>
  value < 0 && dollars > 0 ? `${MINUS}${text}` : text

// Each step picks its unit from the rounded value: 9,999.6 → "10K",
// 999,500 → "1.0M".
const compactDollars = (dollars: number) => {
  if (dollars < 10000) return dollars.toLocaleString("en-US")
  const thousands = Math.round(dollars / 1000)
  if (thousands < 1000) return `${thousands}K`
  const millions = Math.round(dollars / 1e5) / 10
  if (millions < 1000) return `${millions.toFixed(1)}M`
  return `${(Math.round(dollars / 1e8) / 10).toFixed(1)}B`
}

export const formatCompactMoney = (value: number) => {
  const dollars = Math.round(Math.abs(value))
  return withSign(value, dollars, compactDollars(dollars))
}

export const formatFullMoney = (value: number) => {
  const dollars = Math.round(Math.abs(value))
  return withSign(value, dollars, dollars.toLocaleString("en-US"))
}

// A timestamp without a zone is read as UTC; one with a zone keeps it.
export const formatLastActive = (value: string, now: Dayjs = dayjs()) => {
  const date = dayjs.utc(value).local()
  return date.format(date.isSame(now, "year") ? "MMM D" : "MMM D, YYYY")
}

// BED sends prior counts of 0 when it has nothing to compare against, so the
// FE decides: never on school_year; on custom, only while the prior window
// (same length, ending the day before) starts within retention.
export const hasComparablePrior = (
  preset: DashboardPeriodPreset,
  dateFrom: string,
  dateTo: string,
  today: Dayjs = dayjs(),
) => {
  if (preset === "school_year") return false
  if (preset !== "custom") return true
  const from = dayjs(dateFrom)
  const days = dayjs(dateTo).diff(from, "day") + 1
  const retentionStart = today
    .startOf("day")
    .subtract(RETENTION_MONTHS, "month")
    .add(1, "day")
  return !from.subtract(days, "day").isBefore(retentionStart, "day")
}

export type CountTrend = "up" | "down" | "new"

export const getCountTrend = (
  count: number | null,
  prior: number | null,
): CountTrend | null => {
  if (count === null || prior === null || count === prior) return null
  if (prior === 0) return "new"
  return count > prior ? "up" : "down"
}

// BED's own share covers this week only; the period share comes from the
// same headcount as the "no bonuses" count, so it's derived here.
export const getBonusEarnedShare = (
  activeStudents: number,
  withoutBonuses: number,
) => (activeStudents === 0 ? null : 1 - withoutBonuses / activeStudents)

// The first class names the cell, the rest are counted; a class without a name
// has nothing to show, so it's left out.
export const getClassLabel = (classes: IDashboardStudentClass[]) => {
  const names = classes
    .map((studentClass) => studentClass.name?.trim() ?? "")
    .filter(Boolean)
  if (names.length === 0) return null
  return { names, more: names.length - 1, full: names.join(", ") }
}

const uniqueSorted = (ids: number[]) =>
  Array.from(new Set(ids)).sort((a, b) => a - b)

// `st_classes` is a comma-joined list of class ids; anything else in it is
// dropped, and the ids are kept sorted so one selection has one URL.
export const parseClassIds = (raw: string | null) =>
  uniqueSorted(
    (raw ?? "")
      .split(",")
      .filter((token) => /^\d+$/.test(token))
      .map(Number),
  )

export const formatClassIds = (ids: number[]) =>
  ids.length === 0 ? null : uniqueSorted(ids).join(",")

// "Grade 2" before "Grade 10"; equal names keep BED's order.
export const sortClassOptions = (classes: { id: number; name: string }[]) =>
  classes
    .map(({ id, name }) => ({ id, name: name.trim() }))
    .sort((a, b) =>
      a.name.localeCompare(b.name, undefined, {
        numeric: true,
        sensitivity: "base",
      }),
    )
